/* eslint-disable no-console */
import { isDev } from '@/core/config/env'

type DebugNamespace = string

const enabledNamespaces = new Set<string>()
let allEnabled = true

/**
 * 디버그 네임스페이스 활성화/비활성화
 *
 * @example
 * debugEnable('api', 'store')  // api, store만 활성화
 * debugEnable('*')              // 전부 활성화 (기본)
 * debugDisable('api')           // api 비활성화
 */
export function debugEnable(...namespaces: string[]) {
  if (namespaces.includes('*')) {
    allEnabled = true
    return
  }
  allEnabled = false
  namespaces.forEach((ns) => enabledNamespaces.add(ns))
}

export function debugDisable(...namespaces: string[]) {
  namespaces.forEach((ns) => enabledNamespaces.delete(ns))
}

function isEnabled(ns: DebugNamespace): boolean {
  if (!isDev) return false
  return allEnabled || enabledNamespaces.has(ns)
}

const COLORS: Record<string, string> = {
  api: '#3b82f6',
  store: '#8b5cf6',
  form: '#10b981',
  auth: '#f59e0b',
  render: '#ef4444',
  perf: '#ec4899',
}

const DEFAULT_COLOR = '#6b7280'

function getColor(ns: DebugNamespace): string {
  return COLORS[ns] || DEFAULT_COLOR
}

/**
 * 네임스페이스별 조건부 로깅
 * - production에서 자동 비활성
 * - 네임스페이스별 색상 구분
 *
 * @example
 * debugLog('api', 'GET /products', { page: 1 })
 * debugLog('store', '상태 변경', nextState)
 * debugLog('form', '유효성 검사 실패', errors)
 */
export function debugLog(
  ns: DebugNamespace,
  message: string,
  ...data: unknown[]
) {
  if (!isEnabled(ns)) return

  const color = getColor(ns)
  console.info(
    `%c[${ns}]%c ${message}`,
    `color: ${color}; font-weight: bold;`,
    'color: inherit;',
    ...data
  )
}

/**
 * 배열/객체를 테이블 형태로 출력
 *
 * @example
 * debugTable('products', productList)
 */
export function debugTable(ns: DebugNamespace, data: unknown[]) {
  if (!isEnabled(ns)) return

  const color = getColor(ns)
  console.info(`%c[${ns}] 표:`, `color: ${color}; font-weight: bold;`)
  console.table(data)
}

/**
 * 함수 실행 시간 측정 + 로깅
 *
 * @example
 * const result = await debugTime('api', 'fetchProducts', () => fetchProducts())
 */
export async function debugTime<T>(
  ns: DebugNamespace,
  label: string,
  fn: () => T | Promise<T>
): Promise<T> {
  if (!isEnabled(ns)) return fn() as Promise<T>

  const start = performance.now()
  const result = await fn()
  const duration = (performance.now() - start).toFixed(2)

  let color = '#22c55e'
  if (Number(duration) > 1000) {
    color = '#ef4444'
  } else if (Number(duration) > 300) {
    color = '#eab308'
  }

  console.info(
    `%c[${ns}]%c ${label} — %c${duration}ms`,
    `color: ${getColor(ns)}; font-weight: bold;`,
    'color: inherit;',
    `color: ${color}; font-weight: bold;`
  )

  return result
}

/**
 * 상태 스냅샷 비교
 *
 * @example
 * debugDiff('store', '장바구니', prevState, nextState)
 */
export function debugDiff(
  ns: DebugNamespace,
  label: string,
  prev: Record<string, unknown>,
  next: Record<string, unknown>
) {
  if (!isEnabled(ns)) return

  const changes: Record<string, { from: unknown; to: unknown }> = {}
  const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)])

  for (const key of allKeys) {
    if (prev[key] !== next[key]) {
      changes[key] = { from: prev[key], to: next[key] }
    }
  }

  if (Object.keys(changes).length === 0) {
    debugLog(ns, `${label} — 변경 없음`)
    return
  }

  const color = getColor(ns)
  console.info(
    `%c[${ns}]%c ${label} 변경:`,
    `color: ${color}; font-weight: bold;`,
    'color: inherit;'
  )
  console.table(changes)
}
